import FlatCard from "components/UI-lib/FlatCard";
import { classNames } from "components/utils";
import { OrgContext } from "contexts/OrgContext";
import React, { useContext, useEffect, useMemo, useState } from "react";
import agent, { UsageResp, UsageTableRow } from "server";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { BarList } from "components/Graphs/BarList";
import UsernameDisplayField from "components/UsernameDisplayField";

function calc(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

interface DeploymentUsage {
  DeploymentID: string;
  DeploymentName: string;
  ModelName: string;
  DeploymentCost: number;
  CreatedByUserID: string;
}

interface DeploymentDetails {
  name: string;
  modelName: string;
  createdByUserId: string;
}

export const DeploymentUsageTable: React.FC<{
  usageData: UsageResp[];
  usageLoading: boolean;
}> = ({ usageData, usageLoading }) => {
  const orgContext = useContext(OrgContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [deploymentDetails, setDeploymentDetails] = useState<
    Record<string, DeploymentDetails>
  >({});
  const [loadingDeployments, setLoadingDeployments] = useState<
    Record<string, boolean>
  >({});
  const itemsPerPage = 10;

  const loadDeployment = async (deploymentId: string) => {
    if (deploymentDetails[deploymentId] || loadingDeployments[deploymentId])
      return;

    setLoadingDeployments((prev) => ({ ...prev, [deploymentId]: true }));
    try {
      const res = await agent.Deployments.getDeployment(
        orgContext.activeOrgId,
        deploymentId,
        true
      );
      if (res.success && res.data) {
        setDeploymentDetails((prev) => ({
          ...prev,
          [deploymentId]: {
            name: res.data.name || "",
            modelName: res.data.artifact?.model?.model_name || "",
            createdByUserId: res.data.labels?.["userId"] || "",
          },
        }));
      }
    } catch (error) {
      console.error(`Failed to load deployment ${deploymentId}:`, error);
    } finally {
      setLoadingDeployments((prev) => ({ ...prev, [deploymentId]: false }));
    }
  };

  const transformUsages = (usages: UsageResp[]): DeploymentUsage[] => {
    const result: Record<string, DeploymentUsage> = {};

    usages
      .filter((usage) => usage.UsageType === "deployment")
      .forEach((usage) => {
        if (!result[usage.EnvID]) {
          result[usage.EnvID] = {
            DeploymentID: usage.EnvID,
            DeploymentName: "",
            ModelName: "",
            DeploymentCost: 0,
            CreatedByUserID: "",
          };
        }

        result[usage.EnvID].DeploymentCost = parseFloat(usage.Cost || "0.00");

        // Load deployment details if not already loaded
        loadDeployment(usage.EnvID);
      });

    return Object.values(result);
  };

  const transformedUsages = useMemo(() => {
    return transformUsages(usageData).sort((a, b) => {
      return b.DeploymentCost - a.DeploymentCost;
    });
  }, [usageData]);

  const currentUsages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return transformedUsages.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, transformedUsages]);

  const totalPages = Math.ceil(transformedUsages.length / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (usageLoading) {
    return (
      <FlatCard className="w-full justify-center" isLoading={usageLoading}>
        <div className="flex w-full justify-center">
          <span className="text-sm text-gray-700 dark:text-secondary">
            Fetching Deployment Data...
          </span>
        </div>
      </FlatCard>
    );
  }

  if (currentUsages.length === 0) {
    return (
      <FlatCard className="w-full justify-center">
        <div className="flex w-full justify-center">
          <span className="text-sm text-gray-700 dark:text-secondary">
            No Deployments With Usage Found
          </span>
        </div>
      </FlatCard>
    );
  }

  return (
    <>
      <div className="flex flex-row w-full mt-3 mb-5">
        <TopModels
          currentUsages={transformedUsages}
          loadingDeployments={loadingDeployments}
          deploymentDetails={deploymentDetails}
        />
      </div>
      <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>ID</TableHeaderCell>
              <TableHeaderCell>Model</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Created By</TableHeaderCell>
              <TableHeaderCell>Total Cost</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentUsages.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary ">
                    {item.DeploymentID}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {loadingDeployments[item.DeploymentID] ? (
                      <span className="animate-pulse">Loading...</span>
                    ) : (
                      deploymentDetails[item.DeploymentID]?.modelName ||
                      "Unknown"
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {loadingDeployments[item.DeploymentID] ? (
                      <span className="animate-pulse">Loading...</span>
                    ) : (
                      deploymentDetails[item.DeploymentID]?.name || "Unknown"
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {loadingDeployments[item.DeploymentID] ? (
                    <span className="animate-pulse">Loading...</span>
                  ) : (
                    <UsernameDisplayField
                      className="text-secondary"
                      userId={
                        deploymentDetails[item.DeploymentID]?.createdByUserId ||
                        ""
                      }
                    />
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.DeploymentCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableRoot>
      <div className="flex justify-end mt-3 mr-5 items-center">
        <div onClick={handlePreviousPage} className="mr-3 mt-[2px]">
          <FlatCard
            noBorder={true}
            className={classNames(
              currentPage === 1 ? "hidden" : "cursor-pointer",
              "border border-gray-500 dark:border-zinc-800",
              "w-full flex items-center",
              "px-2 py-2",
              "text-xs",
              "h-7"
            )}
          >
            <span>Back</span>
          </FlatCard>
        </div>
        <span className="">{` Page ${currentPage} of ${totalPages} `}</span>
        <div onClick={handleNextPage} className="ml-3 mt-[2px]">
          <FlatCard
            noBorder={true}
            className={classNames(
              currentPage === totalPages ? "hidden" : "cursor-pointer",
              "border border-gray-500 dark:border-zinc-800",
              " w-full flex items-center",
              "px-2 py-2",
              "text-xs",
              "h-7"
            )}
          >
            <span>Next</span>
          </FlatCard>
        </div>
      </div>
    </>
  );
};

interface TopInstancesProps {
  currentUsages: DeploymentUsage[];
  loadingDeployments: Record<string, boolean>;
  deploymentDetails: Record<string, DeploymentDetails>;
}

const TopModels: React.FC<TopInstancesProps> = ({
  currentUsages,
  loadingDeployments,
  deploymentDetails,
}) => {
  const topModels = useMemo(() => {
    return currentUsages
      .sort((a, b) => b.DeploymentCost - a.DeploymentCost)
      .slice(0, 3)
      .map((env) => ({
        name: `${
          deploymentDetails[env.DeploymentID]?.modelName || env.DeploymentID
        }`,
        value: env.DeploymentCost,
      }));
  }, [currentUsages, deploymentDetails, loadingDeployments]);

  return (
    <div className="flex flex-row w-full">
      <div className="flex flex-col w-full mr-3">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">
            Highest Deployment Cost
          </h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={topModels}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
        />
      </div>
    </div>
  );
};
